import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import * as vega from 'vega';
import * as vegaLite from 'vega-lite';
import * as vl from 'vega-lite-api';
import { Handler } from 'vega-tooltip';
// import { json } from "d3";

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')

const brush = vl.selectInterval().encodings('x');
const click = vl.selectMulti().encodings('color');

const colorScale = {
    domain: ['a passes', 'c fails', 'b does not apply'],
    range: ['#00FF00', '#e83838', '#bbb']
}

const config = {
    axis: {
        domain: false,
        tickColor: 'lightGray',
    },
    style: {
        'guide-label': {
            fontSize: 12,
            fill: '#000'
        },
        'guide-title': {
            fontSize: 18,
            fill: '#000'
        }
    },
    legend: {
        disable: true
    }
};

const pointSize = 150;




// registration
vl.register(vega, vegaLite, {
    view: { renderer: 'svg' },
    init: view => { view.tooltip(new Handler().call); }
});

const GraphApiMeasureReportsInteractive = ({ data, graphParams }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const [vegaSpec, setVegaSpec] = useState(null);

    const [marks, setMarks] = useState(null);
    const [vegaView, setVegaView] = useState(null);

    for (let i = 0; i < data.length; i++) {
        let result = data[i].result;
        if (result === 1) {
            data[i].applies_string = 'a passes';
        } else if (result === 0) {
            data[i].applies_string = 'c fails';
        } else {
            data[i].applies_string = 'b does not apply';
        }
    }

    // -------------------------------------------------------------------------------------


    const reportPlot = vl.markPoint({
        filled: true,
        stroke: false,
        size: pointSize,
        opacity: 0.75
    })
        .data(data)
        .transform(
            {"filter": "datetime(datum.unit_back_in_service) > datetime(2024, 8, 6)"},
            {
                timeUnit: 'monthyear',
                field: 'unit_back_in_service',
                as: 'month_creation'
            },
            {
                joinaggregate: [
                    {
                        op: 'mean',
                        field: 'result',
                        as: 'aggregate_percent'
                    }
                ],
                groupby: ['month_creation']
            }
        )
        .encode(
            vl.color().value('lightgray')
                .if(brush, vl.color().fieldN('applies_string').scale(colorScale).title('Result')),
            vl.x().fieldT('unit_back_in_service')
                .timeUnit({ unit: 'monthdateyear', utc: false })
                .scale({padding: pointSize/40})
                .axis({title: null}),
            vl.y().fieldT('unit_back_in_service')
                .timeUnit({ unit: 'hoursminutes', utc: false })
                .scale({padding: pointSize/40})
                .axis({title: 'Time of Day', format: '%I %p'}),
            vl.tooltip().fieldN('incident_number')
        )
        .width(700)
        .height(250)
        .select(brush)
        .transform(vl.filter(click));

    const timeSeriesPlot = vl.markLine({
        point: { filled: true, size: pointSize }
    })
        .data(data)
        .transform(
            {"filter": "datetime(datum.unit_back_in_service) > datetime(2024, 8, 6)"},
            {
                timeUnit: 'monthyear',
                field: 'unit_back_in_service',
                as: 'month_creation'
            },
            {
                aggregate: [
                    {
                        op: 'mean',
                        field: 'result',
                        as: 'mean_result'
                    }
                ],
                groupby: ['month_creation']
            }
        )
        .encode(
            vl.color().value('#e83838'),
            vl.x().fieldT('month_creation')
                .title('Time Span')
                .timeUnit('monthyear'),
            vl.y().fieldQ('mean_result')
                .title('Percent Compliance')
                .scale({domain: [0, 1]})
                .axis({format: '.0%', title: 'Percent Compliance'}),
            vl.tooltip().fieldN('mean_result')
                .format('.2%')
        )
        .width(700)
        .height(250);

    const barPlot = vl.markBar()
        .data(data)
        .encode(
            vl.color().value('lightgray')
                .if(click, vl.color().fieldN('applies_string').scale(colorScale).title('Result')),
            vl.x().count()
                .axis({title: 'Number of Reports'}),
            vl.y().fieldN('applies_string')
                .title('Result')
                .scale({domain: colorScale.domain})
        )
        .width(700)
        .select(click)
        .transform(vl.filter(brush));

    // -------------------------------------------------------------------------------------

    useEffect(() => {
        const vegaLiteSpec = vl.vconcat(reportPlot, timeSeriesPlot, barPlot)
            .config(config)
            .toSpec();
        setVegaSpec(vegaLite.compile(vegaLiteSpec).spec);
    }, [data]);

    useEffect(() => {
        if (vegaSpec) {
            const runtime = vega.parse(vegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg")
                .initialize(containerRef.current)
                .hover()
                .tooltip(new Handler().call);

            view.addEventListener("click", (event, item) => {
                console.log("Point clicked");
                if (item.datum.report_id !== undefined && item.datum.report_id !== null) {
                    navigate('/report/' + item.datum.report_id);
                }
            });

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [vegaSpec]);

    useEffect(() => {
        if (marks) {
            marks.render().then(view => {
                setVegaView(view);
                console.log('Vega view set again');
            }).catch(error => {
                console.error('Error rendering Vega view:', error);
            });
        }
    }, [marks]);


    return (
        <div>
            Legend: + Read, o Unread
            <div ref={containerRef} />
        </div>
    );
};

export default GraphApiMeasureReportsInteractive;